import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Brand & report templates surface (T-0112 / C20-122, E05-F02).
 *
 * Wires the firm-admin brand editor to the real backend (PR #133):
 *
 *   - GET  /api/v1/admin/brand-templates/active
 *       → { brand_template_id, firm_name, header_text, footer_left,
 *           footer_right, cover_layout, type_pairing, colors[],
 *           active, version, created_at, created_by }
 *   - PUT  /api/v1/admin/brand-templates/active
 *       → { brand_template_id, version } — **mints a NEW version row**
 *         and flips the prior active row to inactive (ADR-009 forward-
 *         only; frozen pdf_reports keep resolving the brand profile
 *         they were rendered under).
 *   - GET  /api/v1/admin/brand-templates
 *       → { templates: [{ brand_template_id, version, active,
 *                          created_at, created_by }, ...] }
 *
 * The editor binds against the existing `BrandProfile` shape — the
 * extra `brand_template_id` and `active` fields the backend returns are
 * preserved (passthrough). The PUT body sends `{ theme: { ... } }` per
 * the backend contract and surfaces the minted version via the
 * mutation result.
 */

// ===========================================================================
// Profile schema — what GET /active returns.
// ===========================================================================

export const BrandColorTokenSchema = z.object({
  /** Stable id — matches the prototype's `palette[].id` slugs. */
  id: z.string(),
  /** Display name (e.g. "Avero navy"). */
  name: z.string(),
  /** CSS-variable name (e.g. "--ink-900"). */
  token: z.string(),
  /** Hex value (#RRGGBB). */
  hex: z.string(),
  /** Free-form usage description. */
  use: z.string(),
});
export type BrandColorToken = z.infer<typeof BrandColorTokenSchema>;

export const CoverLayoutSchema = z.enum([
  'full-bleed-band',
  'minimal-wordmark',
  'cover-photo-overlay',
]);
export const TypePairingSchema = z.enum(['inter-source-serif', 'inter-only', 'system-ui']);

export const BrandProfileSchema = z
  .object({
    /** Active brand-template row id. */
    brand_template_id: z.string().uuid(),
    /** Firm name shown in the PDF cover. */
    firm_name: z.string(),
    /** Header text shown on every page beyond the cover. */
    header_text: z.string(),
    /** Footer-left text (variable-templated). */
    footer_left: z.string(),
    /** Footer-right text (variable-templated). */
    footer_right: z.string(),
    /** Cover layout selector. */
    cover_layout: CoverLayoutSchema,
    /** Body / type pairing selector. */
    type_pairing: TypePairingSchema,
    /** Color tokens, ordered as displayed in the swatches grid. */
    colors: z.array(BrandColorTokenSchema),
    /** Active flag — always true on the /active endpoint. */
    active: z.boolean(),
    /** Version label (e.g. "v1.0"). */
    version: z.string(),
    /** ISO timestamp when this profile row was created. */
    created_at: z.string(),
    /** User id of the publisher (null for the seed row). */
    created_by: z.string().nullable(),
  })
  // Tolerate forward-compatible additions on the read side.
  .passthrough();
export type BrandProfile = z.infer<typeof BrandProfileSchema>;

/**
 * `updated_at` is the editor's existing field for the "last published"
 * label. The backend returns `created_at` (each PUT mints a new row, so
 * `created_at` IS the last-published timestamp). Surface a read-only
 * derived alias for back-compat with the editor's `updated_at` use.
 */
export function brandProfileUpdatedAt(profile: BrandProfile): string {
  return profile.created_at;
}

// ===========================================================================
// Registry — list every published version (active + historical).
// ===========================================================================

export const BrandTemplateRegistryRowSchema = z.object({
  brand_template_id: z.string().uuid(),
  version: z.string(),
  active: z.boolean(),
  created_at: z.string(),
  created_by: z.string().nullable(),
});

const ListBrandTemplatesResponseSchema = z.object({
  templates: z.array(BrandTemplateRegistryRowSchema),
});

/**
 * UI registry row — keeps the editor's pre-existing prop shape stable
 * (`id`, `name`, `kind`, `used`, `version`, `status`, `used_in`,
 * `updated_at`) by hydrating the backend rows. The backend only stores
 * the version pointer; v1 hydrates the descriptive fields with derived
 * defaults until per-template metadata lands.
 */
export interface BrandTemplate {
  id: string;
  name: string;
  kind: 'Pulse' | 'Snapshot' | 'Readiness' | 'Health' | 'Archive';
  used: string;
  version: string;
  status: 'Published' | 'Draft';
  used_in: number;
  updated_at: string;
}

/**
 * Hydrate a backend `brand_templates` row into the registry shape the
 * editor table renders. v1 derives:
 *   - `name` / `kind` / `used` / `used_in` — placeholders (the backend
 *     ships one row per firm-active publish, not per template kind).
 *     The future "per-template registry" ticket can widen the API
 *     shape to carry these fields.
 *   - `status` — Published (every backend row is a real publish).
 *
 * This keeps the existing registry view legible while the backend
 * stays minimal. When the backend extends the registry shape, swap
 * this hydration for a passthrough.
 */
function hydrateRegistryRow(r: z.infer<typeof BrandTemplateRegistryRowSchema>): BrandTemplate {
  return {
    id: r.brand_template_id,
    name: `Brand profile ${r.version}`,
    kind: 'Pulse',
    used: r.active ? 'Currently active' : 'Superseded',
    version: r.version,
    status: 'Published',
    used_in: r.active ? 1 : 0,
    updated_at: r.created_at,
  };
}

export function listBrandTemplates(): Promise<BrandTemplate[]> {
  return apiClient('/api/v1/admin/brand-templates', ListBrandTemplatesResponseSchema).then((res) =>
    res.templates.map(hydrateRegistryRow),
  );
}

export function useBrandTemplatesQuery() {
  return useQuery({
    queryKey: ['brand-templates', 'list'] as const,
    queryFn: listBrandTemplates,
    staleTime: 60_000,
  });
}

// ===========================================================================
// Active profile read.
// ===========================================================================

export function getBrandProfile(): Promise<BrandProfile> {
  return apiClient('/api/v1/admin/brand-templates/active', BrandProfileSchema);
}

export function useBrandProfileQuery() {
  return useQuery({
    queryKey: ['brand-templates', 'active'] as const,
    queryFn: getBrandProfile,
    staleTime: 60_000,
  });
}

// ===========================================================================
// PUT — publish a new active version. Returns the minted version id +
// label so the success toast can surface "saved as vX.Y".
// ===========================================================================

const PublishBrandProfileResponseSchema = z.object({
  brand_template_id: z.string().uuid(),
  version: z.string(),
});
export type PublishBrandProfileResult = z.infer<typeof PublishBrandProfileResponseSchema>;

export function publishBrandProfile(profile: BrandProfile): Promise<PublishBrandProfileResult> {
  const theme = {
    firm_name: profile.firm_name,
    header_text: profile.header_text,
    footer_left: profile.footer_left,
    footer_right: profile.footer_right,
    cover_layout: profile.cover_layout,
    type_pairing: profile.type_pairing,
    colors: profile.colors,
  };
  return apiClient('/api/v1/admin/brand-templates/active', PublishBrandProfileResponseSchema, {
    method: 'PUT',
    body: { theme },
  });
}

export function usePublishBrandProfileMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishBrandProfile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['brand-templates'] });
    },
  });
}
